/**
 * FABSpeedDial — FAB with a long-press speed-dial menu (Material 3).
 *
 * Interaction model:
 *   • Tap → fires the primary (first) action immediately.
 *   • Long-press (350ms) → opens menu with all actions; primary highlighted.
 *   • Backdrop tap / Esc → close menu.
 *
 * Visual cue: a small caret in the FAB's lower-right corner hints that
 * more options are available via long-press (Material 3 affordance).
 *
 * PersonSimple:
 *   • Menu is a <div role="menu"> with focus-trap while open.
 *   • Each action is role="menuitem", arrow-key nav, Enter/Space activates.
 *   • Esc closes and returns focus to the FAB.
 *   • Backdrop has aria-hidden=true (decorative dim only).
 *
 * Render order via Portal so the menu/backdrop overlay everything.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { createPortal } from "react-dom";
import { FAB } from "./FAB";
import { useHaptic } from "./useHaptic";

export type FABAction = {
  icon: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  label: string;
  onClick: () => void;
  testId?: string;
  /** Optional sub-label (one-liner under the main label). */
  hint?: string;
};

export type FABSpeedDialProps = {
  icon: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  /** ARIA label for the FAB button itself. */
  label: string;
  /** Actions — first is the "primary" (single-tap target). 2-4 recommended. */
  actions: FABAction[];
  testId?: string;
};

export function FABSpeedDial({ icon, label, actions, testId = "fab-speed-dial" }: FABSpeedDialProps) {
  const [open, setOpen] = useState(false);
  const haptic = useHaptic();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const fabButtonRef = useRef<HTMLElement | null>(null);

  // Track FAB button DOM element so we can return focus on close.
  useEffect(() => {
    fabButtonRef.current = document.querySelector(`[data-testid="${testId}"]`);
  }, [testId, open]);

  const close = useCallback(() => {
    setOpen(false);
    fabButtonRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    if (actions.length <= 1) {
      // Single-action: long-press is a no-op (would duplicate primary tap).
      return;
    }
    setOpen(true);
  }, [actions.length]);

  const fireAction = useCallback(
    (action: FABAction) => {
      haptic.selection();
      action.onClick();
      setOpen(false);
    },
    [haptic],
  );

  const handlePrimary = useCallback(() => {
    if (actions.length === 0) return;
    fireAction(actions[0]);
  }, [actions, fireAction]);

  // Esc to close + focus trap inside menu.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "Tab" && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll<HTMLElement>(
          "[role='menuitem']:not([disabled])",
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const focusables = menuRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']");
        if (!focusables || focusables.length === 0) return;
        const idx = Array.from(focusables).indexOf(document.activeElement as HTMLElement);
        const nextIdx =
          e.key === "ArrowDown"
            ? Math.min(idx + 1, focusables.length - 1)
            : Math.max(idx - 1, 0);
        focusables[nextIdx]?.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    // Focus first item when menu opens.
    requestAnimationFrame(() => {
      const first = menuRef.current?.querySelector<HTMLElement>("[role='menuitem']");
      first?.focus();
    });
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, close]);

  return (
    <>
      <FAB
        icon={icon}
        label={label}
        onClick={handlePrimary}
        onLongPress={actions.length > 1 ? openMenu : undefined}
        testId={testId}
      >
        {actions.length > 1 && (
          // Caret affordance: small triangle in lower-right hints "more on long-press".
          <span
            aria-hidden="true"
            className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-accent-on/70"
          />
        )}
      </FAB>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-40 bg-bg-base/55 backdrop-blur-md transition-opacity duration-200"
            data-testid={`${testId}-backdrop`}
            aria-hidden="true"
            onClick={close}
          >
            <div
              ref={menuRef}
              role="menu"
              aria-label={label}
              data-testid={`${testId}-menu`}
              className="absolute right-4 flex flex-col-reverse gap-2.5"
              style={{ bottom: "calc(env(safe-area-inset-bottom) + 140px)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map((action, idx) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  data-testid={action.testId ?? `${testId}-action-${idx}`}
                  onClick={() => fireAction(action)}
                  className={`group flex items-center gap-3 pl-4 pr-5 py-3 min-h-[48px] rounded-full bg-bg-surface border border-bg-border shadow-[0_8px_22px_rgba(0,0,0,0.4)] text-fg-primary text-left transition-transform duration-300 will-change-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                    idx === 0 ? "border-accent/40" : ""
                  }`}
                  style={{
                    transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                    transitionDelay: `${idx * 30}ms`,
                    animation: `fab-action-in 300ms cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 30}ms both`,
                  }}
                >
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${
                      idx === 0 ? "bg-accent text-accent-on" : "bg-bg-raised text-fg-secondary"
                    }`}
                  >
                    <action.icon size={18} strokeWidth={2.2} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight">{action.label}</span>
                    {action.hint && (
                      <span className="block text-[11px] text-fg-secondary leading-snug mt-0.5">
                        {action.hint}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
      <style>{`
        @keyframes fab-action-in {
          from { opacity: 0; transform: translateY(12px) scale(0.92); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
